import * as React from "react";
import { CheckIcon, Loader2Icon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAutocompleteQuery } from "../../services/api";

/*
 * Shadcn equivalent of the MUI <AutocompleteInput> for remote-resource
 * dropdowns. The MUI one stays around for dialogs that haven't migrated;
 * new shadcn pages (resources / flows filters, etc.) use this.
 *
 * Input-style trigger: the field itself is the search box. Focus or any
 * keystroke opens an anchored Popover with the current results; typing
 * debounces 400ms and re-fetches via Redux. Selecting an option sets the
 * controlled `value`, snaps the input back to that label, and closes the
 * popover. The X button inside the input clears.
 *
 * The Popover.onOpenChange handler also runs on outside click (Radix), so
 * tabbing away or clicking elsewhere closes the panel without help from
 * the consumer.
 */

interface ComboboxOption {
  id?: string | number;
  title?: string;
  [key: string]: unknown;
}

interface RemoteComboboxProps {
  /** Backend resource path passed to getAutocompleteResults (e.g. "resource/name-autocomplete"). */
  resource: string;
  /** Currently selected option. `null` clears the input. */
  value: ComboboxOption | null;
  onChange: (next: ComboboxOption | null) => void;
  /** Field label rendered above the input. */
  label?: React.ReactNode;
  /** Placeholder shown when no value is selected and the input is empty. */
  placeholder?: string;
  /** Custom display function, mirrors MUI AutocompleteInput.getOptionLabel. */
  getOptionLabel?: (option: ComboboxOption) => string;
  disabled?: boolean;
  /** Extra container className. */
  className?: string;
}

const DEBOUNCE_MS = 400;

function defaultGetOptionLabel(option: ComboboxOption | null) {
  return option?.title ?? "";
}

function sameOption(
  a: ComboboxOption | null,
  b: ComboboxOption | null
): boolean {
  if (!a || !b) return a === b;
  if (a.id != null && b.id != null) return a.id === b.id;
  return a === b;
}

export function RemoteCombobox({
  resource,
  value,
  onChange,
  label,
  placeholder,
  getOptionLabel = defaultGetOptionLabel,
  disabled,
  className,
}: RemoteComboboxProps) {
  const { t } = useTranslation();

  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState<string>(() =>
    value ? getOptionLabel(value) : ""
  );
  // Debounced search term — only updates DEBOUNCE_MS after the user
  // stops typing. Drives the RTK Query cache key.
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>("");

  // Keep the displayed text in sync with the selected value when it
  // changes externally (e.g. parent's Clear button setting it to null).
  React.useEffect(() => {
    setInputText(value ? getOptionLabel(value) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    if (!open) return undefined;
    const id = window.setTimeout(() => {
      setDebouncedSearch(inputText);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [open, inputText]);

  const { data: results, isFetching } = useAutocompleteQuery(
    { resource, q: debouncedSearch },
    { skip: !open },
  ) as { data?: ComboboxOption[]; isFetching: boolean };

  const loading = isFetching;

  function handleSelect(option: ComboboxOption) {
    onChange(option);
    setInputText(getOptionLabel(option));
    setOpen(false);
  }

  function handleClear() {
    onChange(null);
    setInputText("");
    setOpen(false);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputText(e.target.value);
    if (!open) setOpen(true);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      // Closing without a fresh selection — snap the displayed text back
      // to the currently-selected option's label (or empty if cleared).
      setInputText(value ? getOptionLabel(value) : "");
    }
  }

  const options = Array.isArray(results) ? results : [];
  const showSpinner = loading && options.length === 0;

  return (
    <div className={cn("space-y-1", className)}>
      {label ? (
        <label className="text-sm font-medium">{label}</label>
      ) : null}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              value={inputText}
              onChange={handleInputChange}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
              spellCheck={false}
              className={cn(value && "pr-8")}
            />
            {value ? (
              <button
                type="button"
                onClick={handleClear}
                aria-label={t("common.clear", "Clear")}
                className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
              >
                <XIcon className="size-3.5" />
              </button>
            ) : null}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-1"
          align="start"
          // Keep focus in the input so the user can keep typing after the
          // popover appears.
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {showSpinner ? (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" />
              {t("common.loading")}
            </div>
          ) : options.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {t("common.no-options")}
            </div>
          ) : (
            <ul role="listbox" className="max-h-64 overflow-auto">
              {options.map((option, index) => {
                const labelText = getOptionLabel(option);
                const optionKey = option.id ?? `${labelText}-${index}`;
                const isSelected = sameOption(value, option);
                return (
                  <li
                    key={optionKey}
                    role="option"
                    aria-selected={isSelected}
                    // onMouseDown instead of onClick so the input's blur
                    // doesn't fire before this handler runs and close the
                    // popover prematurely.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(option);
                    }}
                    className={cn(
                      "flex cursor-pointer items-center rounded px-2 py-1.5 text-sm hover:bg-accent",
                      isSelected && "bg-accent/60"
                    )}
                  >
                    <CheckIcon
                      className={cn(
                        "mr-2 size-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {labelText}
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
